import Data from 'data';
import { skillAuraStats, skillCalc, skillFormula, skillPassiveStats } from 'logic/skills/calc';
import skillFunctions from '.';
import { defaultAnimation } from '../animation';
import { DamageCalculator, missileDamage } from '../damage';
import { DPSCalculator, missileDPS } from '../dps';
import { defaultInfo } from '../skillinfo';

const functions = {};

// Feral Rage/Maul

functions[120] = {
  showActive: id => !!Data.skills[id].aurastate,
  active: {
    stacks: 0,
  },
  maxStacks(id, stats, level) {
    return skillFormula(Data.skills[id], 'clc2', stats, level);
  },
  aura(id, stats, level, active) {
    level = Math.min((active || this.active).stacks || 0, level);
    if (!level) return;
    const skill = Data.skills[id];
    return {
      level,
      state: skill.aurastate,
      stats: () => skillAuraStats(skill, stats, level),
    };
  },
  damage(skill, stats, weapon, level, target) {
    const calc = new DamageCalculator(stats, weapon, false, true);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.skillbonus('damagepercent', skillFormula(skill, 'clc1', stats, level));
    calc.convert(skill.etype, skillFormula(skill, 'clc4', stats, level));
    calc.srcdam(skill.srcdam);
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    return calc.calculate(target);
  },
};

// Rabies

functions[121] = {
  damage(skill, stats, weapon, level, target) {
    const calc = new DamageCalculator(stats, weapon, false, true);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.srcdam(128);
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    return calc.calculate(target);
  },
};

// Hunger

functions[122] = {
  damage(skill, stats, weapon, level, target) {
    const calc = new DamageCalculator(stats, weapon, false, true);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.srcdam(128);
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    calc.skillbonus('skill_physdamage', skillFormula(skill, 'clc1', stats, level))
    return calc.calculate(target);
  },
};

// Volcano

functions[123] = {
  damage(skill, stats, weapon, level, target) {
    return missileDamage(Data.missileByName[skill.srvmissilea], stats, weapon, level, target);
  },
  dps() {},
};

// Armageddon, Hurricane

functions[124] = {
  damage(skill, stats, weapon, level, target) {
    if (skill.srvmissilea) {
      return missileDamage(Data.missileByName[skill.srvmissilea], stats, weapon, level, target);
    } else {
      const calc = new DamageCalculator(stats, weapon, false, false);
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
      return calc.calculate(target);
    }
  },
  dps(id, stats, level, target, info) {
    const skill = Data.skills[id];
    if (skill.srvmissilea) {
      const missile = Data.missileByName[skill.srvmissilea];
      if (!missile) return;
      return missileDPS(skill, missile, stats, level, target, info);
    } else if (info?.damage) {
      const calc = new DPSCalculator();
      calc.damage('damage', info.damage.damage, {interval: skill.param4});
      return calc.calculate(stats, target);
    }
  },
};

// Wake of Fire (Sentry Skill)

functions[125] = {
  damage(skill, stats, weapon, level, target) {
    return missileDamage(Data.missileByName[skill.srvmissilea], stats, weapon, level, target);
  },
};

// Vine Attack

functions[130] = {
  damage(skill, stats, weapon, level, target) {
    return missileDamage(Data.missileByName[skill.srvmissilea], stats, weapon, level, target);
  },
};

// Fenris Rage

functions[137] = {
  showActive: id => !!Data.skills[id].aurastate,
  active: {
    active: true,
  },
  aura(id, stats, level, active) {
    if (!(active || this.active).active) return;
    const skill = Data.skills[id];
    return {
      level,
      state: skill.aurastate,
      stats: () => skillAuraStats(skill, stats, level),
    };
  },
};

// Hydra

functions[144] = {
  summonData(skill, stats, level, target) {
    const sumstats = {base: {}, skills: {}, base_skills: {}, weapon1: {weaponspeed: 0}, class: skill.summon};
    for (let i = 1; i <= 5; ++i) {
      const sid = skill[`sumskill${i}`];
      if (sid) {
        const slevel = skillCalc(skill, skill[`sumsk${i}calc`], stats, level);
        sumstats.skills[sid] = slevel;
        sumstats.base_skills[sid] = slevel;
      }
    }
    Object.assign(sumstats.base, skillPassiveStats(skill, stats, level));

    const sid = skill.sumskill1;
    const sumskill = Data.skills[sid];
    if (!sumskill) return;
    const func = skillFunctions[sumskill.srvdofunc || 0];
    if (!func) return;
    return {stats: sumstats, info: (func.info || defaultInfo).call(func, sid, sumstats, sumstats.skills[sid], target)};
  },

  info(id, stats, level, target) {
    const skill = Data.skills[id];
    const monster = Data.monsters[skill.summon];
    if (!monster) return;

    const summonData = this.summonData(skill, stats, level, target);
    if (!summonData) return;

    const info = summonData.info;
    info.animation = defaultAnimation(skill, stats);
    info.dps = this.dps(id, stats, level, target, info);

    return info;
  },
  dps(id, stats, level, target, info) {
    if (!info.damage || !info.animation) return;
    const skill = Data.skills[id];
    const summonData = this.summonData(skill, stats, level, target);
    if (!summonData) return;

    const duration = skillFormula(skill, 'ln12', stats, level);
    const hits = 3 * Math.floor(duration / summonData.info.animation.fpa) * 0.8;
    const calc = new DPSCalculator();
    calc.damage('missile', info.damage.damage, {interval: info.animation.fpa, count: hits});
    return calc.calculate(summonData.stats, target);
  },
};

// Necromage Missile

functions[149] = {
  damage(skill, stats, weapon, level, target) {
    const result = {};
    const baseid = Data.missileByName[skill.srvmissilea].id;
    for (let i = 0; i < 4; ++i) {
      const damage = missileDamage(Data.missiles[baseid + i], stats, weapon, level, target);
      for (const id in damage) {
        damage[id].label = id;
        damage[id].sumskill = i + 1;
        result[`sk${i}_${id}`] = damage[id];
      }
    }
    return result;
  },
  dps(id, stats, level, target, info) {
    if (!info?.damage || !info.animation) return;
    const skill = Data.skills[id];
    const baseid = Data.missileByName[skill.srvmissilea].id;
    return [1, 2, 3, 4].map(idx => {
      const damage = {};
      for (const dmg of Object.values(info.damage)) {
        if (dmg.sumskill === idx) {
          damage[dmg.label] = dmg;
        }
      }
      const dps = missileDPS(skill, Data.missiles[baseid + idx - 1], stats, level, target, {damage, animation: info.animation});
      dps.sumskill = idx;
      return dps;
    });
  },
};

// Smite

functions[150] = {
  damage(skill, stats, weapon, level, target) {
    if (!weapon) return null;
    const calc = new DamageCalculator(stats, weapon, false, false);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.skillbonus('damagepercent', skillFormula(skill, 'clc1', stats, level));
    calc.smitedamage();
    return calc.calculate(target);
  },
};

export default functions;
